import { useCallback, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { api, ApiError } from '@/lib/api';

// ── Types (mirror /api/admin responses) ──────────────────────────────────────

interface AdminStats {
  generatedAt: string;
  users: { total: number; new7d: number; new30d: number };
  claims: {
    total: number;
    byStatus: Record<string, number>;
    byOutcome: Record<string, number>;
  };
  documents: { total: number; rejected: number };
  packs: { total: number };
  payments: {
    byStatus: Record<string, { count: number; amount: number }>;
    revenueByProduct: Array<{ productType: string; count: number; amount: number }>;
    completedRevenue: number;
    currency: string;
  };
}

interface AuditEntry {
  ts?: string;
  type?: string;
  userId?: string | null;
  ip?: string | null;
  outcome?: string;
  [key: string]: unknown;
}

interface AuditResult {
  persisted: boolean;
  persistenceConfigured: boolean;
  date: string | null;
  availableDates: string[];
  total: number;
  returned: number;
  entries: AuditEntry[];
}

interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  emailVerified: boolean;
  createdAt: string;
  claims: number;
  payments: number;
  totalSpent: number;
}

interface UsersResult {
  total: number;
  limit: number;
  offset: number;
  currency: string;
  users: AdminUser[];
}

const USERS_PAGE_SIZE = 50;

const TYPE_FILTERS = [
  { label: 'All', value: '' },
  { label: 'Auth', value: 'auth' },
  { label: 'Claims', value: 'claim' },
  { label: 'Documents', value: 'document' },
  { label: 'Payments', value: 'payment' },
  { label: 'Packs', value: 'pack' },
  { label: 'AI calls', value: 'ai' },
  { label: 'Admin', value: 'admin' },
];

// Reserved keys shown as dedicated columns; everything else is "details".
const RESERVED_KEYS = new Set(['ts', 'type', 'userId', 'ip', 'outcome']);

function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);
  } catch {
    return `£${amount.toFixed(2)}`;
  }
}

function formatTime(ts?: string): string {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleTimeString('en-GB', { hour12: false });
}

function formatDate(ts?: string): string {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function outcomeClasses(outcome?: string): string {
  if (outcome === 'success') return 'bg-success-50 text-success-700';
  if (outcome === 'failure' || outcome === 'error') return 'bg-error-50 text-error-700';
  return 'bg-surface-100 text-surface-600';
}

function typeClasses(type?: string): string {
  if (!type) return 'bg-surface-100 text-surface-700';
  if (type.startsWith('ai')) return 'bg-brand-50 text-brand-700';
  if (type.startsWith('auth')) return 'bg-blue-50 text-blue-700';
  if (type.startsWith('payment') || type.startsWith('pack')) return 'bg-success-50 text-success-700';
  if (type.startsWith('document')) return 'bg-amber-50 text-amber-700';
  if (type.startsWith('admin')) return 'bg-purple-50 text-purple-700';
  return 'bg-surface-100 text-surface-700';
}

function detailString(entry: AuditEntry): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(entry)) {
    if (RESERVED_KEYS.has(key)) continue;
    const rendered =
      value === null || value === undefined
        ? ''
        : typeof value === 'object'
          ? JSON.stringify(value)
          : String(value);
    if (rendered !== '') parts.push(`${key}=${rendered}`);
  }
  return parts.join('  ');
}

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div className="bg-white rounded-2xl border border-surface-200 p-5">
      <p className="text-sm font-medium text-surface-500">{label}</p>
      <p className="mt-1 text-3xl font-display font-bold text-brand-950">{value}</p>
      {sub && <p className="mt-1 text-xs text-surface-500">{sub}</p>}
    </div>
  );
}

function Breakdown({ title, data }: { title: string; data: Record<string, number> }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((sum, [, n]) => sum + n, 0);

  return (
    <div className="bg-white rounded-2xl border border-surface-200 p-6">
      <h3 className="font-display text-base font-semibold text-brand-950 mb-4">{title}</h3>
      {entries.length === 0 ? (
        <p className="text-sm text-surface-500">No data yet.</p>
      ) : (
        <ul className="space-y-3">
          {entries.map(([key, count]) => {
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            return (
              <li key={key}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-medium text-surface-700 capitalize">
                    {key.replace(/_/g, ' ')}
                  </span>
                  <span className="text-surface-500">
                    {count} <span className="text-surface-400">({pct}%)</span>
                  </span>
                </div>
                <div className="h-2 rounded-full bg-surface-100 overflow-hidden">
                  <div className="h-full bg-brand-500 rounded-full" style={{ width: `${pct}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function AdminPage() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const [audit, setAudit] = useState<AuditResult | null>(null);
  const [auditLoading, setAuditLoading] = useState(true);
  const [auditError, setAuditError] = useState<string | null>(null);

  const [date, setDate] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');

  const [users, setUsers] = useState<UsersResult | null>(null);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState<string>('');
  const [userSearchInput, setUserSearchInput] = useState<string>('');
  const [userOffset, setUserOffset] = useState(0);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const data = await api.get<AdminStats>('/api/admin/stats');
      setStats(data);
    } catch (error) {
      if (error instanceof ApiError && error.status === 403) {
        setForbidden(true);
      } else {
        setStatsError(error instanceof Error ? error.message : 'Failed to load stats');
      }
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    setAuditError(null);
    try {
      const params = new URLSearchParams();
      if (date) params.set('date', date);
      if (typeFilter) params.set('type', typeFilter);
      if (search) params.set('q', search);
      params.set('limit', '500');
      const data = await api.get<AuditResult>(`/api/admin/audit?${params.toString()}`);
      setAudit(data);
      // Adopt the server-selected date (most recent) on first load.
      if (!date && data.date) setDate(data.date);
    } catch (error) {
      if (error instanceof ApiError && error.status === 403) {
        setForbidden(true);
      } else {
        setAuditError(error instanceof Error ? error.message : 'Failed to load audit log');
      }
    } finally {
      setAuditLoading(false);
    }
  }, [date, typeFilter, search]);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const params = new URLSearchParams();
      if (userSearch) params.set('q', userSearch);
      params.set('limit', String(USERS_PAGE_SIZE));
      params.set('offset', String(userOffset));
      const data = await api.get<UsersResult>(`/api/admin/users?${params.toString()}`);
      setUsers(data);
    } catch (error) {
      if (error instanceof ApiError && error.status === 403) {
        setForbidden(true);
      } else {
        setUsersError(error instanceof Error ? error.message : 'Failed to load users');
      }
    } finally {
      setUsersLoading(false);
    }
  }, [userSearch, userOffset]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadAudit();
  }, [loadAudit]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  if (forbidden) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-2xl border border-error-200 p-8 text-center">
          <h1 className="font-display text-xl font-semibold text-error-700">Access denied</h1>
          <p className="mt-2 text-surface-600">
            Your account doesn't have admin access to this dashboard.
          </p>
        </div>
      </div>
    );
  }

  const currency = stats?.payments.currency ?? 'GBP';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-brand-950">Admin</h1>
          <p className="text-sm text-surface-500">
            {stats ? `Snapshot at ${new Date(stats.generatedAt).toLocaleString('en-GB')}` : 'Application overview'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            loadStats();
            loadAudit();
            loadUsers();
          }}
          className="text-sm font-medium px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Stats */}
      {statsLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-2xl border border-surface-200 p-5 animate-pulse">
              <div className="h-4 bg-surface-200 rounded w-1/2" />
              <div className="h-8 bg-surface-200 rounded w-1/3 mt-3" />
            </div>
          ))}
        </div>
      ) : statsError ? (
        <div className="bg-error-50 border border-error-200 rounded-xl p-4 text-error-700 text-sm">
          {statsError}
        </div>
      ) : stats ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Users"
              value={stats.users.total}
              sub={`+${stats.users.new7d} this week · +${stats.users.new30d} this month`}
            />
            <StatCard
              label="Claims"
              value={stats.claims.total}
              sub={`${stats.claims.byStatus['submitted'] ?? 0} submitted`}
            />
            <StatCard
              label="Revenue (completed)"
              value={formatCurrency(stats.payments.completedRevenue, currency)}
              sub={`${stats.packs.total} packs generated`}
            />
            <StatCard
              label="Documents"
              value={stats.documents.total}
              sub={`${stats.documents.rejected} rejected`}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Breakdown title="Claims by status" data={stats.claims.byStatus} />
            <Breakdown title="Claims by outcome" data={stats.claims.byOutcome} />
          </div>

          {/* Payments */}
          <div className="bg-white rounded-2xl border border-surface-200 p-6">
            <h3 className="font-display text-base font-semibold text-brand-950 mb-4">Payments</h3>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-semibold text-surface-400 uppercase tracking-wide mb-2">
                  By status
                </p>
                <ul className="space-y-2 text-sm">
                  {Object.entries(stats.payments.byStatus).length === 0 && (
                    <li className="text-surface-500">No payments yet.</li>
                  )}
                  {Object.entries(stats.payments.byStatus).map(([status, info]) => (
                    <li key={status} className="flex items-center justify-between">
                      <span className="font-medium text-surface-700 capitalize">{status}</span>
                      <span className="text-surface-500">
                        {info.count} · {formatCurrency(info.amount, currency)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-semibold text-surface-400 uppercase tracking-wide mb-2">
                  Completed revenue by product
                </p>
                <ul className="space-y-2 text-sm">
                  {stats.payments.revenueByProduct.length === 0 && (
                    <li className="text-surface-500">No completed payments yet.</li>
                  )}
                  {stats.payments.revenueByProduct.map((p) => (
                    <li key={p.productType} className="flex items-center justify-between">
                      <span className="font-medium text-surface-700 capitalize">
                        {p.productType.replace(/_/g, ' ')}
                      </span>
                      <span className="text-surface-500">
                        {p.count} · {formatCurrency(p.amount, currency)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </>
      ) : null}

      {/* Users */}
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="font-display text-lg font-semibold text-brand-950">Users</h2>
          {users && (
            <span className="text-xs text-surface-500">
              {users.total === 0
                ? 'No users'
                : `${users.offset + 1}–${users.offset + users.users.length} of ${users.total}`}
            </span>
          )}
        </div>

        <form
          className="flex gap-2 mb-4 max-w-md"
          onSubmit={(e) => {
            e.preventDefault();
            setUserOffset(0);
            setUserSearch(userSearchInput.trim());
          }}
        >
          <input
            type="text"
            value={userSearchInput}
            onChange={(e) => setUserSearchInput(e.target.value)}
            placeholder="Search by email or name…"
            className="flex-1 text-sm border border-surface-300 rounded-lg px-3 py-2"
          />
          {userSearch && (
            <button
              type="button"
              onClick={() => {
                setUserSearch('');
                setUserSearchInput('');
                setUserOffset(0);
              }}
              className="text-sm px-3 py-2 text-surface-500 hover:text-surface-700"
            >
              Clear
            </button>
          )}
        </form>

        {usersLoading ? (
          <div className="py-12 text-center text-surface-500 text-sm">Loading users…</div>
        ) : usersError ? (
          <div className="bg-error-50 border border-error-200 rounded-xl p-4 text-error-700 text-sm">
            {usersError}
          </div>
        ) : users && users.users.length > 0 ? (
          <>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold text-surface-400 uppercase tracking-wide border-b border-surface-200">
                    <th className="px-2 py-2">Email</th>
                    <th className="px-2 py-2">Name</th>
                    <th className="px-2 py-2 whitespace-nowrap">Joined</th>
                    <th className="px-2 py-2 text-center">Verified</th>
                    <th className="px-2 py-2 text-right">Claims</th>
                    <th className="px-2 py-2 text-right whitespace-nowrap">Spent</th>
                  </tr>
                </thead>
                <tbody>
                  {users.users.map((u) => (
                    <tr key={u.id} className="border-b border-surface-100 hover:bg-surface-50">
                      <td className="px-2 py-2 font-medium text-brand-950 break-all">{u.email}</td>
                      <td className="px-2 py-2 text-surface-600">{u.name || '—'}</td>
                      <td className="px-2 py-2 whitespace-nowrap text-surface-500">
                        {formatDate(u.createdAt)}
                      </td>
                      <td className="px-2 py-2 text-center">
                        {u.emailVerified ? (
                          <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-success-50 text-success-700">
                            Yes
                          </span>
                        ) : (
                          <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-surface-100 text-surface-500">
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right text-surface-700">{u.claims}</td>
                      <td className="px-2 py-2 text-right whitespace-nowrap text-surface-700">
                        {u.totalSpent > 0
                          ? formatCurrency(u.totalSpent, users.currency)
                          : <span className="text-surface-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {users.total > USERS_PAGE_SIZE && (
              <div className="flex items-center justify-between mt-4">
                <button
                  type="button"
                  disabled={userOffset === 0}
                  onClick={() => setUserOffset(Math.max(0, userOffset - USERS_PAGE_SIZE))}
                  className="text-sm font-medium px-3 py-1.5 rounded-lg border border-surface-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-surface-50"
                >
                  Previous
                </button>
                <span className="text-xs text-surface-500">
                  Page {Math.floor(userOffset / USERS_PAGE_SIZE) + 1} of{' '}
                  {Math.max(1, Math.ceil(users.total / USERS_PAGE_SIZE))}
                </span>
                <button
                  type="button"
                  disabled={userOffset + USERS_PAGE_SIZE >= users.total}
                  onClick={() => setUserOffset(userOffset + USERS_PAGE_SIZE)}
                  className="text-sm font-medium px-3 py-1.5 rounded-lg border border-surface-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-surface-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="py-12 text-center text-surface-500 text-sm">
            No users{userSearch ? ' match your search' : ''}.
          </div>
        )}
      </div>

      {/* Audit log */}
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="font-display text-lg font-semibold text-brand-950">Audit log</h2>
          {audit && (
            <span className="text-xs text-surface-500">
              {audit.returned} of {audit.total} entries
              {audit.date ? ` · ${audit.date}` : ''}
            </span>
          )}
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <select
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="text-sm border border-surface-300 rounded-lg px-3 py-2 bg-white"
            aria-label="Audit log date"
          >
            {(audit?.availableDates?.length ? audit.availableDates : date ? [date] : []).map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
            {(!audit?.availableDates || audit.availableDates.length === 0) && (
              <option value="">Today</option>
            )}
          </select>

          <div className="flex flex-wrap gap-1.5">
            {TYPE_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setTypeFilter(f.value)}
                className={clsx(
                  'text-xs font-medium px-3 py-1.5 rounded-full transition-colors',
                  typeFilter === f.value
                    ? 'bg-brand-600 text-white'
                    : 'bg-surface-100 text-surface-700 hover:bg-surface-200'
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          <form
            className="flex-1 min-w-[200px] flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setSearch(searchInput.trim());
            }}
          >
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search (user id, claim id, label…)"
              className="flex-1 text-sm border border-surface-300 rounded-lg px-3 py-2"
            />
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setSearchInput('');
                }}
                className="text-sm px-3 py-2 text-surface-500 hover:text-surface-700"
              >
                Clear
              </button>
            )}
          </form>
        </div>

        {/* Persistence warning */}
        {audit && !audit.persistenceConfigured && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800 text-sm mb-4">
            Audit persistence isn't configured on this environment (<code>AUDIT_LOG_DIR</code> is
            unset), so events stream to stdout instead of a file. Set it on a persistent disk to
            read entries here.
          </div>
        )}

        {/* Table */}
        {auditLoading ? (
          <div className="py-12 text-center text-surface-500 text-sm">Loading entries…</div>
        ) : auditError ? (
          <div className="bg-error-50 border border-error-200 rounded-xl p-4 text-error-700 text-sm">
            {auditError}
          </div>
        ) : audit && audit.entries.length > 0 ? (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold text-surface-400 uppercase tracking-wide border-b border-surface-200">
                  <th className="px-2 py-2 whitespace-nowrap">Time</th>
                  <th className="px-2 py-2">Type</th>
                  <th className="px-2 py-2">Outcome</th>
                  <th className="px-2 py-2 whitespace-nowrap">User</th>
                  <th className="px-2 py-2 whitespace-nowrap">IP</th>
                  <th className="px-2 py-2">Details</th>
                </tr>
              </thead>
              <tbody>
                {audit.entries.map((entry, i) => (
                  <tr key={i} className="border-b border-surface-100 align-top hover:bg-surface-50">
                    <td className="px-2 py-2 whitespace-nowrap text-surface-500" title={entry.ts}>
                      {formatTime(entry.ts)}
                    </td>
                    <td className="px-2 py-2">
                      <span
                        className={clsx(
                          'inline-block text-xs font-medium px-2 py-0.5 rounded-full',
                          typeClasses(entry.type)
                        )}
                      >
                        {entry.type ?? '—'}
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      {entry.outcome ? (
                        <span
                          className={clsx(
                            'inline-block text-xs font-medium px-2 py-0.5 rounded-full',
                            outcomeClasses(entry.outcome)
                          )}
                        >
                          {entry.outcome}
                        </span>
                      ) : (
                        <span className="text-surface-300">—</span>
                      )}
                    </td>
                    <td
                      className="px-2 py-2 whitespace-nowrap font-mono text-xs text-surface-500"
                      title={entry.userId ?? undefined}
                    >
                      {entry.userId ? `${entry.userId.slice(0, 8)}…` : '—'}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap font-mono text-xs text-surface-500">
                      {entry.ip ?? '—'}
                    </td>
                    <td className="px-2 py-2 font-mono text-xs text-surface-600 break-all">
                      {detailString(entry) || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-12 text-center text-surface-500 text-sm">
            No entries{search || typeFilter ? ' match your filters' : ' for this day'}.
          </div>
        )}
      </div>
    </div>
  );
}
